import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, CircularProgress, Alert, TextField, Chip, Tooltip,
    IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { getAllUsers } from '../api/admin';
import axios from 'axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));


const UserRow = ({ user, onEditTrial }) => {
    const trialEndDate = user.trial_end ? dayjs(user.trial_end) : null;
    const daysLeft = trialEndDate ? trialEndDate.diff(dayjs(), 'day') : 0;
    const nextChargeDate = (user.subscription_status === 'on_trial' || user.subscription_status === 'trialing')
        ? trialEndDate?.format('MM/DD/YYYY')
        : user.current_period_end ? dayjs(user.current_period_end).format('MM/DD/YYYY') : 'N/A';

    return (
        <TableRow>
            <TableCell>{user.company_name}</TableCell>
            <TableCell>{user.email}</TableCell>
            <TableCell sx={{ textTransform: 'capitalize' }}>
                <Chip
                    label={user.role}
                    size="small"
                    sx={{
                        fontSize: '0.65rem',
                        height: '20px',
                        backgroundColor:
                            user.role === 'shipper' ? '#c8e6c9' : 
                            user.role === 'carrier' ? '#fff9c4' : 
                            user.role === 'broker'  ? '#bbdefb' : '#e0e0e0', 
                        color:
                            user.role === 'shipper' ? '#1b5e20' : 
                            user.role === 'carrier' ? '#f57f17' : 
                            user.role === 'broker'  ? '#0d47a1' : '#000000', 
                    }}
                />
            </TableCell>
            <TableCell>
                <Chip
                    label={user.subscription_status || 'inactive'}
                    size="small"
                    color={
                        (user.subscription_status === 'on_trial' || user.subscription_status === 'trialing') ? 'info' :
                        user.subscription_status === 'active' ? 'success' :
                        user.subscription_status === 'past_due' ? 'warning' :
                        'default'
                    }
                />
            </TableCell>
            <TableCell align="center">
                {(user.subscription_status === 'on_trial' || user.subscription_status === 'trialing') && daysLeft > 0 ? `${daysLeft} days` : 'N/A'}
            </TableCell>
            <TableCell>{nextChargeDate}</TableCell>
            <TableCell>
                <Tooltip title={user.stripe_subscription_id || 'Not Available'}>
                    <span>{user.stripe_subscription_id ? `${user.stripe_subscription_id.substring(0, 10)}...` : 'N/A'}</span>
                </Tooltip>
            </TableCell>
            <TableCell>
                {(() => {
                    const status = user.subscription_status;
                    if (user.cancel_at_period_end) {
                        return <Chip icon={<CancelIcon />} label="Subscription Canceled" color="error" size="small" variant="outlined" />;
                    } else if (status === 'active' || status === 'on_trial' || status === 'trialing') {
                        return <Chip icon={<CheckCircleIcon />} label="Using Subscription" color="success" size="small" variant="outlined" />;
                    } else if (status === 'canceled') {
                        return <Chip icon={<CancelIcon />} label="Subscription Canceled" color="error" size="small" variant="outlined" />;
                    } else {
                        return <Chip label={status || 'Inactive'} size="small" variant="outlined" />;
                    }
                })()}
            </TableCell>
            <TableCell>
                <IconButton size="small" onClick={() => onEditTrial(user)}>
                    <EditIcon fontSize="small" />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

const TrialBilling = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [globalTrialDays, setGlobalTrialDays] = useState('');
    const [currentTrialPeriod, setCurrentTrialPeriod] = useState(null);
    const [trialDialogOpen, setTrialDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [trialAdjustmentDays, setTrialAdjustmentDays] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchUsersAndSettings = async () => {
        try {
            setLoading(true);
            const data = await getAllUsers();
            setUsers((data.users || []).filter(user => user.role === 'carrier'));
            setCurrentTrialPeriod(data.currentTrialDays); 
        } catch (err) {
            setError(err.message || 'Failed to fetch data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsersAndSettings();
    }, []);

    const handleSetGlobalTrial = async () => {
        if (!globalTrialDays || isNaN(globalTrialDays)) {
            setSnackbar({ open: true, message: "Please enter a valid number of days.", severity: 'warning' });
            return;
        }

        try {
            const token = localStorage.getItem('jwtToken');
            await axios.post(`${API_BASE_URL}/api/admin/global-trial-period`, 
                { days: globalTrialDays }, 
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSnackbar({ open: true, message: `Global default updated. New users will receive ${globalTrialDays} days trial.`, severity: 'success' });
            fetchUsersAndSettings(); 
            setGlobalTrialDays('');
        } catch (err) {
            console.error(err);
            setError('Failed to update global trial period.');
        }
    };

    const handleOpenTrialDialog = (user) => {
        setSelectedUser(user);
        setTrialAdjustmentDays('');
        setTrialDialogOpen(true);
    };

   const handleSaveTrialAdjustment = async () => {
    if (!trialAdjustmentDays) return;
    try {
        const token = localStorage.getItem('jwtToken');
        const res = await axios.put(
            `${API_BASE_URL}/api/admin/users/${selectedUser.role}/${selectedUser.id}/trial`, 
            { days: Number(trialAdjustmentDays) },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        setTrialDialogOpen(false);
        fetchUsersAndSettings();
        const msg = res.data?.stripeWarning
            ? `DB updated, but Stripe sync failed: ${res.data.message}`
            : 'User trial period updated successfully.';
        setSnackbar({ open: true, message: msg, severity: res.data?.stripeWarning ? 'warning' : 'success' });
    } catch (err) {
        const serverMsg = err.response?.data?.message || 'Failed to update user trial.';
        setSnackbar({ open: true, message: serverMsg, severity: 'error' });
    }
};

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box>
            
            <StyledPaper>
                <Typography variant="h6" gutterBottom>Global Trial Settings</Typography>
                <Typography variant="body1" sx={{ mb: 1, color: '#11224d' }}>
                    Current Global Trial Period: <strong style={{ color: '#ffb300' }}>
                        {currentTrialPeriod !== null ? `${currentTrialPeriod} days` : 'Not Set'}
                    </strong>
                </Typography>
                
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Set the default trial period for <strong>new users</strong> only. This does not affect existing accounts.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                        label="Trial Period (Days)"
                        type="number"
                        size="small"
                        value={globalTrialDays}
                        onChange={(e) => setGlobalTrialDays(e.target.value)}
                        sx={{ width: 200 }}
                    />
                    <Button 
                        variant="contained" 
                        onClick={handleSetGlobalTrial}
                        disabled={!globalTrialDays}
                    >
                        Save Default
                    </Button>
                </Box>
            </StyledPaper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#11224d' }}>
                            <TableCell sx={{ color: 'white' }}>Company</TableCell>
                            <TableCell sx={{ color: 'white' }}>Email</TableCell>
                            <TableCell sx={{ color: 'white' }}>Role</TableCell>
                            <TableCell sx={{ color: 'white' }}>Status</TableCell>
                            <TableCell sx={{ color: 'white' }} align="center">Trial Days Left</TableCell>
                            <TableCell sx={{ color: 'white' }}>Next Charge</TableCell>
                            <TableCell sx={{ color: 'white' }}>Subscription ID</TableCell>
                            <TableCell sx={{ color: 'white' }}>User Status</TableCell>
                            <TableCell sx={{ color: 'white' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map(user => (
                            <UserRow
                                key={`${user.role}-${user.id}`}
                                user={user}
                                onEditTrial={handleOpenTrialDialog}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={trialDialogOpen} onClose={() => setTrialDialogOpen(false)}>
                <DialogTitle>Adjust Trial Period</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Extend or reduce the trial for <strong>{selectedUser?.company_name}</strong>. 
                        Enter a positive number to extend, or a negative number to reduce.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Days to Add/Remove"
                        type="number"
                        fullWidth
                        value={trialAdjustmentDays}
                        onChange={(e) => setTrialAdjustmentDays(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTrialDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveTrialAdjustment} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </Box>
    );
};

export default TrialBilling;